<template>
  <div data-private class="flex h-full">
    <PagesExplorer :nodes="nodes" :loading="loading" :error="error || ''" @node-drop="onNodeDrop" />
    <div class="flex-1 h-full overflow-auto bg-gray-100" style="scrollbar-gutter: stable">
      <PagesFolder v-if="subject === 'folder'" :key="`folder-${stem}`" :stem="stem" :pages="mine" />
      <PageHost v-else :key="`${subject}-${subjectId}`" :subject="subject" :subject-id="subjectId" />
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import PageHost from './PageHost.vue';
import PagesFolder from './PagesFolder.vue';
import PagesExplorer from './PagesExplorer.vue';
import { childFolders, domainFolder, folderPages, folderUrl, pageUrl, stemParts } from '@/utils/pageFolders';

export default {
  name: 'PagesView',
  components: { PageHost, PagesFolder, PagesExplorer },
  provide() {
    return { refreshMine: this.fetchMine };
  },
  props: {
    subject: { type: String, default: 'overall' },
    subjectId: { type: Number, default: null },
    stem: { type: String, default: '' },
  },
  data() {
    return { domains: [], mine: [], loading: false, error: null };
  },
  computed: {
    nodes() {
      const tree = this.mineChildren('');
      this.domains.forEach((domain) => {
        const label = domainFolder(domain.name);
        let folder = tree.find((node) => node.key === `folder-${label}`);
        if (!folder) {
          folder = { key: `folder-${label}`, label, stem: label, parentStem: '', draggable: false, icon: ['fas', 'folder'], kind: 'folder', children: [] };
          tree.push(folder);
        }
        folder.to = `/pages/domain/${domain.id}`;
        folder.sensitive = false;
        const metrics = (domain.composable_metrics || [])
          .map((link) => ({ key: `metric-${domain.id}-${link.metric_id}`, label: link.metric_name, to: `/pages/metric/${link.metric_id}`, icon: ['fas', 'chart-bar'], kind: 'metric', draggable: false }))
          .sort((a, b) => a.label.localeCompare(b.label));
        const folders = folder.children.filter((node) => node.kind === 'folder');
        const pages = folder.children.filter((node) => node.kind === 'page');
        folder.children = [...folders, ...metrics, ...pages];
      });
      const folders = tree.filter((node) => node.kind === 'folder').sort((a, b) => {
        if (a.order !== null && a.order !== undefined && b.order !== null && b.order !== undefined && a.order !== b.order) return a.order - b.order;
        if (a.order !== null && a.order !== undefined) return -1;
        if (b.order !== null && b.order !== undefined) return 1;
        return a.label.localeCompare(b.label);
      });
      const pages = tree.filter((node) => node.kind === 'page');
      return [...folders, ...pages];
    },
  },
  mounted() {
    this.fetchDomains();
    this.fetchMine();
  },
  methods: {
    visibility(page) {
      if (page.shared && Array.isArray(page.entity_ids) && page.entity_ids.length) {
        const n = page.entity_ids.length;
        return { icon: ['fas', 'building'], tone: 'text-amber-600', hint: `Shared with ${n} ${n === 1 ? 'entity' : 'entities'} & children` };
      }
      if (page.shared) return { icon: ['fas', 'globe'], tone: 'text-primary-600', hint: 'Public: everyone in the tenant can open it' };
      return { icon: ['fas', 'user'], tone: 'text-gray-700', hint: 'Just yours: private to you' };
    },
    mineChildren(stem) {
      const folders = childFolders(this.mine, stem).map((folder) => ({
        key: `folder-${folder.stem}`, label: folder.name, stem: folder.stem, parentStem: stem, order: folder.order,
        draggable: true, to: folderUrl(folder.stem), icon: ['fas', 'folder'], sensitive: true, kind: 'folder', children: this.mineChildren(folder.stem),
      }));
      const pages = folderPages(this.mine, stem).map((page) => ({
        key: `page-${page.id}`, pageId: page.id, stem: page.stem || '', parentStem: stem, order: page.portal_order,
        draggable: true, label: page.title, to: pageUrl(page), sensitive: true, kind: 'page', ...this.visibility(page),
      }));
      return [...folders, ...pages];
    },
    async onNodeDrop({ source, target, position }) {
      this.error = null;
      try {
        if (source.kind === 'page' && source.pageId) {
          const destStem = (target.kind === 'folder' && position === 'inside') ? (target.stem || '') : (target.parentStem || '');
          const siblings = folderPages(this.mine.filter((p) => p.id !== source.pageId), destStem);
          let idx = siblings.length;
          if (target.kind === 'page' && position !== 'inside') {
            const tIdx = siblings.findIndex((p) => p.id === target.pageId);
            if (tIdx !== -1) idx = position === 'before' ? tIdx : tIdx + 1;
          } else if (target.kind === 'folder' && position === 'before') {
            idx = 0;
          }
          const movedPage = this.mine.find((p) => p.id === source.pageId) || { id: source.pageId };
          siblings.splice(idx, 0, movedPage);
          const items = siblings.map((p, i) => ({
            id: p.id,
            portal_order: (i + 1) * 10,
            ...(p.id === source.pageId ? { stem: destStem } : {}),
          }));
          await axios.post('/api/impact_analytics/agent-pages/reorder/', { items });
          await this.fetchMine();
        } else if (source.kind === 'folder' && source.stem) {
          const leaf = stemParts(source.stem).slice(-1)[0];
          if (target.kind === 'folder' && position === 'inside') {
            const to = target.stem ? `${target.stem}/${leaf}` : leaf;
            if (to !== source.stem && !to.startsWith(`${source.stem}/`)) {
              await axios.post('/api/impact_analytics/agent-pages/move-stem/', { from: source.stem, to });
            }
          } else {
            const destParent = target.parentStem || '';
            const to = destParent ? `${destParent}/${leaf}` : leaf;
            if (to !== source.stem && !to.startsWith(`${source.stem}/`)) {
              await axios.post('/api/impact_analytics/agent-pages/move-stem/', { from: source.stem, to });
            }
            const sibFolders = childFolders(this.mine, destParent).filter((f) => f.stem !== source.stem && f.stem !== to);
            let fIdx = sibFolders.length;
            if (target.kind === 'folder') {
              const ti = sibFolders.findIndex((f) => f.stem === target.stem);
              if (ti !== -1) fIdx = position === 'before' ? ti : ti + 1;
            }
            sibFolders.splice(fIdx, 0, { stem: to, name: leaf });
            const items = [];
            sibFolders.forEach((f, i) => {
              const base = (i + 1) * 100;
              const under = this.mine
                .filter((p) => (p.stem || '') === f.stem || String(p.stem || '').startsWith(`${f.stem}/`))
                .sort((a, b) => (a.portal_order ?? 999999) - (b.portal_order ?? 999999) || a.id - b.id);
              under.forEach((p, j) => items.push({ id: p.id, portal_order: base + j }));
            });
            if (items.length) await axios.post('/api/impact_analytics/agent-pages/reorder/', { items });
          }
          await this.fetchMine();
        }
      } catch (err) {
        this.error = err?.response?.data?.error || 'Could not move that item.';
      }
    },
    async fetchDomains() {
      try {
        const { data } = await axios.get('/api/performancedomains/');
        this.domains = data;
      } catch {
        this.error = 'Failed to load performance domains';
      }
    },
    async fetchMine() {
      this.loading = true;
      this.error = null;
      try {
        const { data } = await axios.get('/api/impact_analytics/agent-pages/');
        this.mine = data.filter((page) => !page.subject_type);
      } catch {
        this.error = 'Failed to load pages';
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
